import type { Client, DateIso, Phase } from '../../types/domain';
import { addDays, dateFromIso, isoStr } from '../dates';

// Alerte de fin de phase (arbitrage du 02/10/2026) : une phase close dès sa date de fin ne compte
// plus les heures qui arrivent ensuite. La page Projets signale les phases qui se terminent dans
// les 30 jours, et celles déjà terminées qui reçoivent encore des heures.
export const ENDING_SOON_DAYS = 30;

/** Heures saisies (manuelles et Jira) après la fin d'une phase, et date de la dernière. */
export interface LateHours {
  heures: number;
  derniereDate: DateIso;
}

export interface PhaseEndAlert {
  client: Client;
  phase: Phase;
  daysLeft?: number;
  lateHours?: LateHours;
}

// Phase annulée : clôturée avant d'avoir commencé (fin antérieure au début).
const isCancelled = (ph: Phase) => !!(ph.dateDebut && ph.dateFin && ph.dateFin < ph.dateDebut);

const daysBetween = (from: DateIso, to: DateIso) =>
  Math.round((dateFromIso(to).getTime() - dateFromIso(from).getTime()) / 86400000);

/** Phases qui se terminent entre aujourd'hui et dans `days` jours, la plus proche d'abord. */
export function phasesEndingSoon(
  clients: Client[] | null | undefined,
  todayIso: DateIso,
  days: number = ENDING_SOON_DAYS,
): PhaseEndAlert[] {
  const limit = isoStr(addDays(dateFromIso(todayIso), days));
  const list: PhaseEndAlert[] = [];
  for (const client of clients || [])
    for (const phase of client.phases || []) {
      const end = phase.dateFin;
      if (!end || isCancelled(phase) || end < todayIso || end > limit) continue;
      list.push({ client, phase, daysLeft: daysBetween(todayIso, end) });
    }
  return list.sort((a, b) => String(a.phase.dateFin).localeCompare(String(b.phase.dateFin)));
}

/** Phases terminées qui reçoivent des heures après leur fin, la plus récemment touchée d'abord.
    Une phase laissée close ne revient que si le total dépasse celui vu à ce moment-là. */
export function phasesWithLateHours(
  clients: Client[] | null | undefined,
  lateByPhase: Record<string, LateHours>,
): PhaseEndAlert[] {
  const list: PhaseEndAlert[] = [];
  for (const client of clients || [])
    for (const phase of client.phases || []) {
      const late = lateByPhase[phase.id];
      if (!late || !(late.heures > 0)) continue;
      if (late.heures <= (Number(phase.lateHoursSeen) || 0) + 0.001) continue;
      list.push({ client, phase, lateHours: late });
    }
  return list.sort((a, b) =>
    String(b.lateHours?.derniereDate).localeCompare(String(a.lateHours?.derniereDate)),
  );
}
